import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, Package, Truck, BoxIcon, ScanLine, 
  MapPin, CheckCircle, Clock, Download, RefreshCw, 
  ArrowRight, User, Calendar, Hash
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const USNTracking = () => {
  const navigate = useNavigate();
  // Per-section mount sets URL param ?section=<slug>; Salem route leaves it null.
  const { section: sectionSlug } = useParams();
  const sectionQs = sectionSlug ? `?section=${encodeURIComponent(sectionSlug)}` : '';
  const [usnCode, setUsnCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!usnCode.trim()) {
      toast.error('Please enter a USN code');
      return;
    }

    setIsLoading(true);
    setError(null);
    setTrackingData(null);

    try {
      const response = await apiClient.get(`/sections/dispatch/usn-tracking/${usnCode.trim()}${sectionQs}`);
      setTrackingData(response.data);
      toast.success('USN tracking data loaded');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to fetch USN tracking data';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!trackingData) return;

    try {
      const response = await apiClient.get(`/sections/dispatch/usn-tracking-csv/${usnCode.trim()}${sectionQs}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `usn_tracking_${usnCode}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getStageIcon = (stageName) => {
    switch (stageName) {
      case 'Internal Transfer':
        return <Truck className="w-5 h-5" />;
      case 'Bundle Receipt':
        return <ScanLine className="w-5 h-5" />;
      case 'Put Away':
        return <MapPin className="w-5 h-5" />;
      case 'Cancelled Put Away Re-assignment':
        return <RefreshCw className="w-5 h-5" />;
      case 'Picking':
        return <Package className="w-5 h-5" />;
      case 'Packing':
        return <BoxIcon className="w-5 h-5" />;
      case 'DC Tagging / Dispatch':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const getStageColor = (stageName, status) => {
    if (status === 'Completed' || status === 'Packed' || status === 'Dispatched' || status === 'RECEIVED') {
      return 'bg-green-100 border-green-500 text-green-700';
    }
    if (status === 'IN_PROGRESS' || status === 'PICKING') {
      return 'bg-yellow-100 border-yellow-500 text-yellow-700';
    }
    return 'bg-blue-100 border-blue-500 text-blue-700';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  const renderStageDetails = (stage) => {
    const { data } = stage;
    
    const DetailRow = ({ label, value, icon: Icon }) => (
      value ? (
        <div className="flex items-center gap-2 py-1">
          {Icon && <Icon className="w-4 h-4 text-gray-400" />}
          <span className="text-sm text-gray-500 min-w-[120px]">{label}:</span>
          <span className="text-sm font-medium text-gray-800">{value}</span>
        </div>
      ) : null
    );

    switch (stage.stage_name) {
      case 'Internal Transfer':
        return (
          <div className="space-y-1">
            <DetailRow label="Transfer ID" value={data.transfer_id} icon={Hash} />
            <DetailRow label="From Section" value={data.from_section} />
            <DetailRow label="To Section" value={data.to_section} />
            <DetailRow label="Total Bundles" value={data.total_bundles} />
            <DetailRow label="Created By" value={data.created_by_name || data.created_by} icon={User} />
            <DetailRow label="Shipped By" value={data.shipped_by_name || data.shipped_by} icon={User} />
            <DetailRow label="Shipped At" value={formatTimestamp(data.shipped_at)} icon={Calendar} />
            <DetailRow label="Received By" value={data.received_by_name || data.received_by} icon={User} />
            <DetailRow label="Received At" value={formatTimestamp(data.received_at)} icon={Calendar} />
          </div>
        );

      case 'Bundle Receipt':
        return (
          <div className="space-y-1">
            <DetailRow label="Receipt ID" value={data.receipt_entry_id} icon={Hash} />
            <DetailRow label="Bundle Code" value={data.bundle_code} />
            <DetailRow label="USN Count" value={data.usn_count} />
            <DetailRow label="Item Barcode" value={data.item_barcode} />
            <DetailRow label="Source Section" value={data.source_section} />
            <DetailRow label="Label Printed" value={data.label_printed ? 'Yes' : 'No'} />
            <DetailRow label="Received By" value={data.processed_by_name || data.processed_by} icon={User} />
            <DetailRow label="Timestamp" value={formatTimestamp(data.timestamp)} icon={Calendar} />
          </div>
        );

      case 'Put Away':
        return (
          <div className="space-y-1">
            <DetailRow label="Put Away Slip" value={data.put_away_slip_no} icon={Hash} />
            <DetailRow label="Bin Location" value={data.bin_location} icon={MapPin} />
            <DetailRow label="Bundle Code" value={data.bundle_code} />
            <DetailRow label="Item Barcode" value={data.item_barcode} />
            <DetailRow label="Design" value={data.design_name} />
            <DetailRow label="Shade" value={data.shade} />
            <DetailRow label="Size" value={data.size} />
            <DetailRow label="Qty" value={data.qty} />
            <DetailRow label="Put Away By" value={data.processed_by_name || data.processed_by} icon={User} />
            <DetailRow label="Timestamp" value={formatTimestamp(data.timestamp)} icon={Calendar} />
          </div>
        );

      case 'Cancelled Put Away Re-assignment':
        return (
          <div className="space-y-1">
            <DetailRow label="Original Bin" value={data.original_bin} icon={MapPin} />
            <DetailRow label="New Bin" value={data.new_bin} icon={MapPin} />
            <DetailRow label="Reason" value={data.reason} />
            <DetailRow label="Processed By" value={data.processed_by_name || data.processed_by} icon={User} />
            <DetailRow label="Timestamp" value={formatTimestamp(data.timestamp)} icon={Calendar} />
          </div>
        );

      case 'Picking':
        return (
          <div className="space-y-1">
            <DetailRow label="Pick Slip #" value={data.slip_number} icon={Hash} />
            <DetailRow label="SKU Code" value={data.sku_code} />
            <DetailRow label="Customer" value={data.customer_name} />
            <DetailRow label="Picked From" value={data.picked_from_bin} icon={MapPin} />
            <DetailRow label="Picked Qty" value={data.picked_qty} />
            <DetailRow label="Pick Status" value={data.pick_status} />
            <DetailRow label="Picker" value={data.assigned_to_name || data.assigned_to} icon={User} />
            <DetailRow label="Picked At" value={formatTimestamp(data.picked_at)} icon={Calendar} />
          </div>
        );

      case 'Packing':
        return (
          <div className="space-y-1">
            <DetailRow label="Box Number" value={data.box_number} icon={Hash} />
            <DetailRow label="Box ID" value={data.box_id} />
            <DetailRow label="Pick Slip #" value={data.pick_slip_number} />
            <DetailRow label="Customer" value={data.customer_name} />
            <DetailRow label="Items in Box" value={data.total_items_in_box} />
            <DetailRow label="Packed Qty" value={data.packed_qty} />
            <DetailRow label="Packer" value={data.packer_name || data.packer_id} icon={User} />
            <DetailRow label="Timestamp" value={formatTimestamp(data.timestamp)} icon={Calendar} />
          </div>
        );

      case 'DC Tagging / Dispatch':
        return (
          <div className="space-y-1">
            <DetailRow label="DC Number" value={data.dc_number} icon={Hash} />
            <DetailRow label="Transporter" value={data.transporter} icon={Truck} />
            <DetailRow label="Vehicle Number" value={data.vehicle_number} />
            <DetailRow label="Tagged By" value={data.dc_tagged_by_name || data.dc_tagged_by} icon={User} />
            <DetailRow label="Tagged At" value={formatTimestamp(data.dc_tagged_at)} icon={Calendar} />
          </div>
        );

      default:
        return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/dispatch')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">USN Tracking</h1>
              <p className="text-sm text-[#E8DCC8]">Track complete USN movement history</p>
            </div>
          </div>
          {trackingData && (
            <Button 
              onClick={handleExportCSV}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Search Section */}
        <Card className="mb-8 border-[#E5DFD0]">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-display text-[#1A1F2E] flex items-center gap-2">
              <Search className="w-5 h-5 text-[#2D5F4F]" />
              Search USN
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="Enter USN code (e.g., USN001234)"
                value={usnCode}
                onChange={(e) => setUsnCode(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 border-[#E5DFD0] focus:border-[#2D5F4F] focus:ring-[#2D5F4F]"
                data-testid="usn-search-input"
              />
              <Button 
                onClick={handleSearch}
                disabled={isLoading}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white px-8"
                data-testid="search-btn"
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Track
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardContent className="py-6 text-center">
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {trackingData && (
          <>
            {/* Summary Card */}
            <Card className="mb-8 border-[#E5DFD0]">
              <CardHeader className="bg-[#F5F1E8] rounded-t-lg">
                <CardTitle className="text-lg font-display text-[#1A1F2E] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#2D5F4F]" />
                    USN Summary
                  </div>
                  <Badge className={
                    trackingData.summary.current_status === 'Dispatched' ? 'bg-green-500' :
                    trackingData.summary.current_status === 'Packed' ? 'bg-blue-500' :
                    trackingData.summary.current_status.includes('Picking') ? 'bg-yellow-500' :
                    'bg-gray-500'
                  }>
                    {trackingData.summary.current_status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Row 1: USN, Bundle, Item Barcode, Status */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">USN Code</p>
                    <p className="font-mono font-semibold text-[#1A1F2E]">{trackingData.usn_code}</p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Item Barcode</p>
                    <p className="font-mono font-semibold text-[#1A1F2E]">
                      {trackingData.summary.item_barcode || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Bundle Code</p>
                    <p className="font-mono font-semibold text-[#1A1F2E]">
                      {trackingData.summary.bundle_code || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Last Activity</p>
                    <p className="font-semibold text-[#1A1F2E] text-sm">
                      {formatTimestamp(trackingData.summary.last_activity)}
                    </p>
                  </div>
                </div>
                
                {/* Row 2: Design Name, Shade, Size, Description */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Design Name</p>
                    <p className="font-semibold text-[#1A1F2E]">
                      {trackingData.summary.design_name || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Shade</p>
                    <p className="font-semibold text-[#1A1F2E]">
                      {trackingData.summary.shade || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Size</p>
                    <p className="font-semibold text-[#1A1F2E]">
                      {trackingData.summary.size || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#FFFBF5] p-4 rounded-lg border border-[#E5DFD0]">
                    <p className="text-sm text-gray-500">Description</p>
                    <p className="font-semibold text-[#1A1F2E] text-sm truncate" title={trackingData.summary.description}>
                      {trackingData.summary.description || 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="text-lg font-display text-[#1A1F2E] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#2D5F4F]" />
                  Movement Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {trackingData.stages.map((stage, index) => (
                    <div key={stage.stage_number} className="relative pb-8 last:pb-0">
                      {/* Vertical line */}
                      {index < trackingData.stages.length - 1 && (
                        <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-[#E5DFD0]" />
                      )}
                      
                      {/* Stage card */}
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 ${getStageColor(stage.stage_name, stage.status)}`}>
                          {getStageIcon(stage.stage_name)}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 bg-white rounded-lg border border-[#E5DFD0] p-4 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-400">
                                Stage {stage.stage_number}
                              </span>
                              <h3 className="font-semibold text-[#1A1F2E]">
                                {stage.stage_name}
                              </h3>
                            </div>
                            <Badge variant="outline" className={
                              stage.status === 'Completed' || stage.status === 'Packed' || stage.status === 'Dispatched' || stage.status === 'RECEIVED' ? 
                                'border-green-500 text-green-600' :
                              stage.status === 'IN_PROGRESS' || stage.status === 'PICKING' ? 
                                'border-yellow-500 text-yellow-600' :
                                'border-blue-500 text-blue-600'
                            }>
                              {stage.status}
                            </Badge>
                          </div>
                          
                          <div className="border-t border-[#E5DFD0] pt-3">
                            {renderStageDetails(stage)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Show pending stages */}
                  {trackingData.stages.length < 6 && (
                    <div className="mt-4 pt-4 border-t border-dashed border-[#E5DFD0]">
                      <p className="text-sm text-gray-500 mb-3">Pending Stages:</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { num: 1, name: 'Internal Transfer' },
                          { num: 2, name: 'Bundle Receipt' },
                          { num: 3, name: 'Put Away' },
                          { num: 4, name: 'Picking' },
                          { num: 5, name: 'Packing' },
                          { num: 6, name: 'DC Tagging / Dispatch' }
                        ].filter(s => !trackingData.stages.find(st => Math.floor(st.stage_number) === s.num))
                         .map(pending => (
                          <Badge key={pending.num} variant="outline" className="text-gray-400 border-gray-300">
                            {pending.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Empty State */}
        {!trackingData && !error && !isLoading && (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-16 text-center">
              <Search className="w-16 h-16 text-[#E5DFD0] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">Track USN Movement</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Enter a USN code above to view its complete movement history through 
                Internal Transfer, Bundle Receipt, Put Away, Picking, Packing, and Dispatch stages.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default USNTracking;
